import api from '@/api/calendar'
import { getRange, getRangeForJewishCalendar } from '@/helpers/rangeFromDate'
import { uniqBy } from 'lodash'
import { actionTypes as actionTypesFromOrders } from '@/store/modules/orders'
import calendarRangeHandler from '@/helpers/calendarRangeHandler'
import { getStorage } from '@/helpers/Local'
import { actionTypesJewishCalendar } from '@/store/modules/jewish-calendar/types'
import getSelectedLanguage from '@/store/utils/get-selected-language.utils'
import serializeMeetingData from '@/store/utils/serialize-meeting-data.utils'
import { normalizeCalendarType } from '@/helpers/normalize-calendar-type.utils'

const state = () => ({
    ['orders']: {},
    ['pick_up_new']: {},
    ['repairs']: {},
    ['pick_up_repairs']: {},
    ['us_agent']: {},
    meetings: [],
    meetingsV2: [],
    meetingsType: 'orders',
    meetingsSearch: '',
    meetingsRange: {},
    isFetching: false,
    searchQuery: '',
})

export const getterTypes = {
    getAllForOrders: '[calendar] Get all meeting for orders',
    getAllForPickUpNew: '[calendar] Get all meeting for pick up new',
    getAllForRepairs: '[calendar] Get all meeting for repairs',
    getAllForPickUpRepairs: '[calendar] Get all meeting for pick up repairs',
    getAllForUsAgent: '[calendar] Get all meeting for us agent',
    getMeetingsPerDay: '[calendar] Get all meeting for selected type and day',
    getAllMeetings: `[calendar] Get all fetched meetings`,
    isFetching: '[calendar] Is fetching',
}

export const mutationTypes = {
    updateAllMeetings: '[calendar] Change or update all meetings for selected',
    addNewOne: '[calendar] Add the new meeting',
    deleteOne: '[calendar] Delete the selected meeting',
    updateOne: '[calendar] Update the selected meeting',
    clearAllMeetings: '[calendar][mutation] Clear all meetings',
    updateClient: '[calendar] Update client mutation',
    getAllMeetings: '[calendar] Put all fetched meetings',
    updateMeeting: '[calendar] Put updated meeting',
    addMeeting: '[calendar] Put new created meeting',
    startFetching: '[calendar] Start fetching',
    stopFetching: '[calendar] Stop fetching',
    updateSearchQuery: '[calendar] update search query',
    setMeetingsSearch: '[calendar] Set meetings search query',
    setMeetingsType: '[calendar] Set meetings type',
    setMeetingsRange: '[calendar] Set meetings range',
}

export const actionTypes = {
    getAllForSelected: '[calendar] Load all meeting for selected params',
    addNewOne: '[calendar] Add the new meeting',
    deleteOne: '[calendar] Delete selected meeting',
    updateOne: '[calendar] Update the selected meeting',
    updateClient: '[calendar] Update client action',
    updateSearchQuery: '[calendar] update search query',
    setMeetingsSearch: '[calendar] Set meetings search query',
    setMeetingsType: '[calendar] Set meetings type',
    setMeetingsRange: '[calendar] Set meetings range',
    setMeetingsRangeWithoutCall: '[calendar] Set meetings range without call',
    setMeetingsTypeWithoutCall: '[calendar] Set meetings type without call',
}

const getters = {
    [getterTypes.getAllForOrders]: (state) => {
        let meetings = []
        if (Object.keys(state['orders']).length === 0) {
            return []
        }
        Object.keys(state['orders']).forEach((key) => {
            meetings = [...meetings, ...state['orders'][key]]
        })
        return meetings
    },
    [getterTypes.getAllMeetings]: (state) => {
        //TODO: remade after approve
        return state.meetingsV2
    },
    [getterTypes.getAllForPickUpNew]: (state) => {
        let meetings = []
        if (Object.keys(state['pick_up_new']).length === 0) return []
        Object.keys(state['pick_up_new']).forEach((key) => {
            meetings = [...meetings, ...state['pick_up_new'][key]]
        })
        return meetings
    },
    [getterTypes.getAllForRepairs]: (state) => {
        let meetings = []
        if (Object.keys(state['repairs']).length === 0) return []
        Object.keys(state['repairs']).forEach((key) => {
            meetings = [...meetings, ...state['repairs'][key]]
        })
        return meetings
    },
    [getterTypes.getAllForPickUpRepairs]: (state) => {
        let meetings = []
        if (Object.keys(state['pick_up_repairs']).length === 0) return []
        Object.keys(state['pick_up_repairs']).forEach((key) => {
            meetings = [...meetings, ...state['pick_up_repairs'][key]]
        })
        return meetings
    },
    [getterTypes.getAllForUsAgent]: (state) => {
        let meetings = []
        if (Object.keys(state['us_agent']).length === 0) return []
        Object.keys(state['us_agent']).forEach((key) => {
            meetings = [...meetings, ...state['us_agent'][key]]
        })
        return meetings
    },
    [getterTypes.getMeetingsPerDay]: (state) => (type, date) => {
        const lowerDate = date.slice(0, 10)

        const typesToInclude =
            type === 'pick_up_new' ? ['pick_up_new', 'repairs'] : [type]

        return state.meetingsV2.filter((meeting) => {
            const meetingType = normalizeCalendarType(meeting.calendarType)
            const meetingDate = meeting.startAt.slice(0, 10)

            return typesToInclude.includes(meetingType) && meetingDate === lowerDate
        })
    },
    [getterTypes.isFetching]: (state) => {
        return state.isFetching
    },
}

const mutations = {
    [mutationTypes.updateAllMeetings]: (state, { data, type }) => {
        let meetings = {}
        Object.values(data).map((day) => {
            day.forEach((meeting) => {
                meetings[getRange(meeting.start_at)] = [
                    ...(meetings?.[getRange(meeting.start_at)] || []),
                    meeting,
                ]
            })
        })

        Object.keys(meetings).forEach((key) => {
            state[type] = {
                ...state[type],
                [key]: uniqBy(
                    [...(state?.[type]?.[key] || []), ...meetings[key]],
                    'id'
                ),
            }
        })
    },
    [mutationTypes.addNewOne]: (state, meeting) => {
        const typeOfCalendar = meeting.calendar.toLowerCase()
        const range = getRange(meeting.start_at)

        let correctType = ''
        let type = ''
        switch (typeOfCalendar) {
            case 'pick_up_new':
            case 'pick-up new wigs':
                correctType = 'Pick-up New Wigs'
                type = 'pick_up_new'
                break
            case 'pick_up_repairs':
                correctType = 'Pick-up Repairs'
                type = 'pick_up_repairs'
                break
            default:
                type = meeting.calendar.toLowerCase()
                correctType = meeting.calendar
        }

        const newMeeting = {
            [range]: [
                ...(state?.[type]?.[range] || []),
                {
                    ...meeting,
                    calendar: correctType,
                },
            ].filter((i) => i),
        }

        state[type] = {
            ...state[type],
            ...newMeeting,
        }
    },
    [mutationTypes.deleteOne]: (state, id) => {
        state.meetingsV2 = state.meetingsV2.filter(
            (meeting) => meeting.id !== id
        )
    },
    [mutationTypes.clearAllMeetings]: (state) => {
        state['orders'] = {}
        state['pick_up_new'] = {}
        state['repairs'] = {}
        state['pick_up_repairs'] = {}
        state['us_agent'] = {}
        state.meetings = []
    },
    [mutationTypes.updateClient](state, newClient) {
        // Combine meetings from state into a single array
        const meetings = Object.values(state)
            .flatMap((item) => Object.values(item))
            .flat()
        // Update meetings with the new client information
        const updatedMeetings = meetings.map((meeting) => {
            if (meeting.client_id === newClient.id) {
                return { ...meeting, client: newClient }
            }
            return meeting
        })
        // Update state
        Object.keys(state).forEach((key) => {
            state[key] = Object.keys(state[key]).reduce((acc, orderId) => {
                acc[orderId] = state[key][orderId].map((meeting) => {
                    // Find the corresponding updated meeting
                    const updatedMeeting = updatedMeetings.find(
                        (m) => m.id === meeting.id
                    )
                    return updatedMeeting ? updatedMeeting : meeting
                })
                return acc
            }, {})
        })
    },
    [mutationTypes.getAllMeetings](state, meetings) {
        //TODO: remade after approve
        return (state.meetingsV2 = meetings)
    },
    [mutationTypes.addMeeting](state, newMeeting) {
        return (state.meetingsV2 = [
            ...state.meetingsV2,
            serializeMeetingData(newMeeting),
        ])
    },
    [mutationTypes.updateMeeting](state, updatedMeeting) {
        state.meetingsV2 = state.meetingsV2.map((meeting) => {
            if (meeting.id !== updatedMeeting.id) {
                return meeting
            }

            return serializeMeetingData(updatedMeeting)
        })
    },
    [mutationTypes.startFetching](state) {
        state.isFetching = true
    },
    [mutationTypes.stopFetching](state) {
        state.isFetching = false
    },
    [mutationTypes.updateSearchQuery](state, searchQuery) {
        state.searchQuery = searchQuery
    },
    [mutationTypes.setMeetingsRange](state, range) {
        state.meetingsRange = range
    },
    [mutationTypes.setMeetingsType](state, type) {
        state.meetingsType = type
    },
    [mutationTypes.setMeetingsSearch](state, search) {
        state.meetingsSearch = search
    },
}

const actions = {
    [actionTypes.getAllForSelected]: ({ commit, dispatch, state }) => {
        const isUsa = getStorage('access_role') === 'USA_MANAGER'
        const lang = getSelectedLanguage().lang
        const range = state.meetingsRange
        const usedType = state.meetingsType
        const params = {
            type: usedType,
            range: calendarRangeHandler(range),
            search: state.meetingsSearch,
            isUsa,
        }

        commit(mutationTypes.startFetching)

        const fetchOne = (type) => {
            return api.getMeetingsV2({ ...params, type }).then((data) => {
                getRangeForJewishCalendar(range).forEach(({ year, month }) => {
                    dispatch(
                        `jewishCalendar/${actionTypesJewishCalendar.loadAll}`,
                        { year, month, lang },
                        { root: true }
                    )
                })
                return { type, data: data.data }
            })
        }

        const typesToFetch =
            usedType === 'pick_up_new' ? ['pick_up_new', 'repairs'] : [usedType]

        return Promise.all(typesToFetch.map(fetchOne))
            .then((results) => {
                commit(mutationTypes.stopFetching)

                let combinedMeetings = []

                results.forEach(({ type, data }) => {
                    // commit(mutationTypes.updateAllMeetings, {
                    //     data,
                    //     type,
                    //     range,
                    // })

                    combinedMeetings = [...combinedMeetings, ...data]
                })

                commit(mutationTypes.getAllMeetings, combinedMeetings)

                return combinedMeetings
            })
            .catch((err) => {
                commit(mutationTypes.stopFetching)
                throw err
            })
    },
    [actionTypes.addNewOne]: ({ commit, dispatch }, data) => {
        return new Promise((res, rej) => {
            api.addNewOne(data)
                .then((newMeeting) => {
                    commit(mutationTypes.addMeeting, newMeeting)
                    // dispatch(
                    //     `orders/${actionTypesFromOrders.addNewMeeting}`,
                    //     { data, id },
                    //     {
                    //         root: true,
                    //     }
                    // )
                    res()
                })
                .catch((er) => {
                    console.log(er)
                    rej(er?.response?.data?.errors)
                })
        })
    },
    [actionTypes.deleteOne]: ({ commit, dispatch }, { id, wigId }) => {
        return new Promise((res, rej) => {
            api.deleteOne(id)
                .then(() => {
                    res()
                    commit(mutationTypes.deleteOne, id)
                    if (wigId) {
                        dispatch(
                            `orders/${actionTypesFromOrders.reloadSelectedOrder}`,
                            undefined,
                            { root: true }
                        )
                    }
                })
                .catch((er) => {
                    rej(er)
                    console.log(er)
                })
        })
    },
    [actionTypes.updateOne]: ({ commit }, { data, id }) => {
        return new Promise((res, rej) => {
            api.updateOne({ data, id })
                .then((updatedMeeting) => {
                    commit(mutationTypes.updateMeeting, updatedMeeting)
                    res()
                })
                .catch((er) => {
                    rej(rej(er.response?.data?.errors))
                })
        })
    },
    [actionTypes.updateClient]({ commit }, client) {
        commit(mutationTypes.updateClient, client)
    },
    [actionTypes.updateSearchQuery]({ commit }, searchQuery) {
        commit(mutationTypes.updateSearchQuery, searchQuery)
    },
    [actionTypes.setMeetingsRange]({ commit, dispatch }, range) {
        commit(mutationTypes.setMeetingsRange, range)
        dispatch(actionTypes.getAllForSelected)
    },
    [actionTypes.setMeetingsRangeWithoutCall]({ commit, dispatch }, range) {
        commit(mutationTypes.setMeetingsRange, range)
    },
    [actionTypes.setMeetingsType]({ commit, dispatch }, type) {
        commit(mutationTypes.setMeetingsType, type)
        dispatch(actionTypes.getAllForSelected)
    },
    [actionTypes.setMeetingsTypeWithoutCall]({ commit, dispatch }, type) {
        commit(mutationTypes.setMeetingsType, type)
    },
    [actionTypes.setMeetingsSearch]({ commit, dispatch }, search) {
        commit(mutationTypes.setMeetingsSearch, search)
        dispatch(actionTypes.getAllForSelected)
    },
}

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
}
